"use client";

import { useEffect, useCallback, useRef } from "react";
import { useAuth } from "../providers/AuthProvider";
import { apiFetch } from "../lib/api";
import { useStore } from "../store";
import { useChannelMembers, useDisplayNames } from "../store/selectors";
import type { Member } from "../store/types";

export type { Member } from "../store/types";

export function useMembers(channelId: string | null) {
  const { token } = useAuth();
  const members = useChannelMembers(channelId);
  const { userDisplayNames, agentDisplayNames } = useDisplayNames(channelId);
  const tokenRef = useRef(token);
  tokenRef.current = token;

  const fetchMembers = useCallback(async () => {
    const t = tokenRef.current;
    if (!t || !channelId) {
      return;
    }

    useStore.getState().setMembersLoading(channelId, true);
    try {
      const data = await apiFetch<Member[]>(`/channels/${channelId}/members`, t);
      useStore.getState().setMembers(channelId, data);
    } catch {
      useStore.getState().setMembersLoading(channelId, false);
    }
  }, [channelId]);

  useEffect(() => {
    if (channelId) {
      fetchMembers();
    }
  }, [channelId, fetchMembers]);

  return { members, userDisplayNames, agentDisplayNames, refetch: fetchMembers };
}
